/* ============================================================
   sc-pricing-api.js
   Bridges the Sofa configurator to the Vercel pricing calculator,
   and applies per-product option visibility from Shopify metafields.

   All price calculation happens server-side in the Vercel endpoint.
   No cost data, margins, or supplier rates are shipped to the browser.
   ============================================================ */
(function (global) {
  var SC = global.SC = global.SC || {};

  SC.pricingApi = {
    ENDPOINT: 'https://storefront-api-data-6264s-projects.vercel.app/api/pricing',
    DRAFT_ORDER_ENDPOINT: 'https://cdn.sophieconran.com/api/draft-order',

    /* ---- PRICING ---- */
    /* Returns a Promise<{ price, currency, available }>. On network failure
       resolves null so the UI can show "Price on request". */
    getPrice: function (payload) {
      var endpoint = this.ENDPOINT;
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) throw new Error('pricing ' + r.status);
        return r.json();
      }).catch(function (e) {
        /* A blocked preflight and a genuinely unreachable endpoint both
           surface as an opaque TypeError ("Failed to fetch") with no
           status, which is why a CORS problem has previously been
           indistinguishable from a real outage in the console. The
           browser only prints the CORS detail to its own log, not to
           the exception, so infer it and say so explicitly. */
        var looksLikeCors = (e instanceof TypeError) ||
          /failed to fetch|networkerror|load failed/i.test(e.message || '');
        if (looksLikeCors) {
          var origin = (typeof location !== 'undefined') ? location.origin : '(unknown)';
          console.error(
            '[SC pricing] Request blocked before it reached the server. ' +
            'This is almost always CORS, not a pricing fault: the endpoint ' +
            'is not returning Access-Control-Allow-Origin for this origin.\n' +
            '  origin:   ' + origin + '\n' +
            '  endpoint: ' + endpoint + '\n' +
            'Check the OPTIONS preflight in the Network tab. If its response ' +
            'has no Access-Control-Allow-Origin header, the allowlist is the ' +
            'problem, not the payload.'
          );
        } else {
          console.warn('[SC pricing] API unavailable:', e.message);
        }
        return null;
      });
    },

    /* Build the pricing calculator's exact config body from a configurator
       selection.

       TRANSLATION LAYER: the theme's internal option values stay stable
       (share URLs, saved builds and the image resolver depend on them);
       anything the calculator names differently is translated here, at
       the payload boundary only. This block is the single source of truth
       for the theme -> calculator vocabulary. Full mapping tables and the
       verification method: OPTION-MAPPING-2026-07-03.md */
    _slugMaps: {
      size:        { 'armchair': '1-seater', 'loveseat': '1.5-seater' },
      backCushion: { 'fixed': 'fixed-back', 'scatter': 'scatter-back' },
      piping:      { 'oxford': 'oxford-border' },
      /* Cover skirt is derived from the theme's merged cover value. */
      coverToSkirt: { 'fixed': 'no-skirt', 'loose': 'dressmaker',
                      'boxpleat': 'box-pleat', 'ruffle': 'ruffle' },
    },
    _slug: function (dim, value) {
      if (value == null) return null;
      var map = this._slugMaps[dim] || {};
      return map[value] !== undefined ? map[value] : value;
    },

    buildPayload: function (sel, rangeKey, country) {
      var coverValue = sel.cover || null;
      var coverIsFixed = coverValue === 'fixed';
      return {
        range: rangeKey,
        size: this._slug('size', sel.size || null),
        fabric: sel.fabricHandle || sel.fabric || null,
        fabricCode: sel.fabricCode || null,
        cushion: sel.cushion || null,
        backCushion: this._slug('backCushion', sel.backCushion || null),
        cover: coverIsFixed ? 'fixed' : 'loose',
        coverSkirt: this._slugMaps.coverToSkirt[coverValue] || null,
        cushionFill: sel.cushionFill || null,
        armCushionFill: sel.armCushionFill || null,
        backCushionFill: sel.backCushionFill || null,
        legStyle: sel.legStyle || null,
        legFinish: sel.legFinish || null,
        piping: this._slug('piping', sel.piping || null),
        /* Contrast piping fabric: the curated pairing for the selected
           body fabric (SC.contrastPairings). Only sent when a contrast
           piping style is selected; null otherwise. Field name to be
           confirmed with the calculator side. */
        pipingFabric: (sel.piping && String(sel.piping).indexOf('contrast') === 0 &&
          sel.fabricHandle && global.SC && SC.contrastPairings &&
          SC.contrastPairings[sel.fabricHandle]) || null,
        frame: sel.frame || 'standard',
        country: country || 'UK'
      };
    },

    /* ---- OPTION VISIBILITY FROM METAFIELDS ---- */
    applyVisibility: function (rules) {
      var meta = global.SC_PRODUCT_META || {};
      if (!rules) return rules;
      var hiddenGroups = meta.hiddenGroups || [];
      var hiddenValues = meta.hiddenValues || {};
      var bespokeValues = meta.bespokeValues || {};

      var out = JSON.parse(JSON.stringify(rules));
      out.groups = out.groups
        .filter(function (g) { return hiddenGroups.indexOf(g.key) === -1; })
        .map(function (g) {
          var hv = hiddenValues[g.key] || [];
          var bv = bespokeValues[g.key] || [];
          g.options = g.options
            .filter(function (o) { return hv.indexOf(o.value) === -1; })
            .map(function (o) {
              if (bv.indexOf(o.value) !== -1) o.bespoke = true;
              return o;
            });
          return g;
        });
      return out;
    }
  };
})(window);
